/**
 * @file Renders an editable table backed by a form, with per-cell editing.
 */
import React from "react";
import { Form, Table } from "antd";
import type { Key } from "react";
import type { FormInstance, TableProps } from "antd";
import type { ColumnsType } from "antd/es/table";

import CollectionRow from "./CollectionRow";
import CollectionCell from "./CollectionCell";
import type { CellProps, ColumnConfig, CollectionItem, CollectionRef, OnSave } from "./types";

import "./Collection.css";

export const EditableContext = React.createContext<FormInstance | null>(null);

const TABLE_CLASS_NAME = "editable-collection";
const TABLE_SIZE = "middle";

const COMPONENTS = {
  body: {
    row: CollectionRow,
    cell: CollectionCell,
  },
};

type CollectionColumn = ColumnConfig & {
  onCell?: (record: CollectionItem) => CellProps;
};

type OnChange = (selectedRowKeys: Key[], selectedRows: CollectionItem[]) => void;

type Props = {
  items?: CollectionItem[];
  onSave?: OnSave;
  summary?: TableProps<CollectionItem>["summary"];
  onChange?: OnChange;
  tableProps?: Record<string, unknown>;
  collection?: CollectionRef;
  columnsConfig?: ColumnConfig[];
  selectedRowItems?: Key[];
  enableSelectItem?: boolean;
};

/**
 * Renders the editable table, its row selection and its per-cell editing.
 */
const Collection = ({
  items = [],
  onSave,
  summary,
  onChange,
  tableProps = {},
  collection,
  columnsConfig = [],
  selectedRowItems = [],
  enableSelectItem = true,
}: Props): JSX.Element => {
  const [ form ] = Form.useForm();

  const hasCollectionRef = !!collection;

  if (hasCollectionRef) {
    collection.current.form = form;

    /**
     * Validates the form fields and reports whether any of them is invalid.
     */
    collection.current.hasFormError = async () =>
      form.validateFields()
        .then(() => true)
        .catch(() => false);
  }

  /**
   * Tells whether the given row is currently in edit mode.
   */
  const isEditing = (record: CollectionItem): boolean => {
    if (!enableSelectItem) {
      return true;
    }

    const isSelected = selectedRowItems.includes(record.key);

    return isSelected;
  };

  /**
   * Disables the row checkbox when the row itself is disabled.
   */
  const getCheckboxProps = (record: CollectionItem) => {
    const isDisabled = !!record.disabled;

    if (isDisabled) {
      return { disabled: true };
    }

    return {};
  };

  /**
   * Forwards the selected row keys and rows to the change handler, if any.
   */
  const onSelectionChange = (selectedRowKeys: Key[], selectedRows: CollectionItem[]): void => {
    const hasChangeHandler = !!onChange;

    if (!hasChangeHandler) {
      return;
    }

    onChange(selectedRowKeys, selectedRows);
  };

  const rowSelection = {
    selectedRowKeys: selectedRowItems,
    onChange: onSelectionChange,
    getCheckboxProps,
  };

  /**
   * Finds the position of the given row within the items list.
   */
  const getIndexRow = (record: CollectionItem): number => {
    const indexRow = items.findIndex((item) => {
      const hasId = !!item.id;

      if (hasId) {
        return item.id === record.id;
      }

      return item.key === record.key;
    });

    return indexRow;
  };

  const columns: CollectionColumn[] = columnsConfig.map((column) => {
    if (!column.editable) {
      return column;
    }

    const { isShow = () => true } = column;

    /**
     * Builds the editable-cell props for the given row of this column.
     */
    const onCell = (record: CollectionItem): CellProps => ({
      title: column.title,
      input: column.input || {},
      editing: isEditing(record),
      editable: column.editable,
      indexRow: getIndexRow(record),
      dataIndex: column.dataIndex,
      isShow,
      record,
      onSave,
    });

    return {
      ...column,
      onCell,
    };
  });

  /**
   * Builds the table props, adding row selection when it is enabled.
   */
  const getDefaultTableProps = (): TableProps<CollectionItem> => {
    // The editable cell reads its own props, which antd types as the
    // HTML attributes of the cell.
    const tableColumns = columns as unknown as ColumnsType<CollectionItem>;

    const params: TableProps<CollectionItem> = {
      summary,
      columns: tableColumns,
      components: COMPONENTS,
      size: TABLE_SIZE,
      bordered: false,
      pagination: false,
      dataSource: [ ...items ],
    };

    if (!enableSelectItem) {
      return params;
    }

    return {
      ...params,
      rowSelection: {
        ...rowSelection,
        checkStrictly: false,
      },
    };
  };

  const defaultTableProps = getDefaultTableProps();

  return (
    <Form
      form={form}
      component={false}
    >
      <EditableContext.Provider
        value={form}
      >
        <Table
          className={TABLE_CLASS_NAME}
          {...defaultTableProps}
          {...tableProps}
        />
      </EditableContext.Provider>
    </Form>
  );
};

export default Collection;
